/**
 * KasaTab — POS (point-of-sale) panel within the Retail page.
 * Product grid (with live on-hand stock) + search + sticky cart + fiscalize prompt → checkout.
 * Selling more than the on-hand quantity is blocked with a localized "{item} out of stock" toast.
 * Low-stock panel lives in ZalihaTab.
 */
import type { TaxRateCode } from '@gym/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton } from '../../components/ui/Button.js';
import { EmptyState, Skeleton } from '../../components/ui/Feedback.js';
import { SearchInput } from '../../components/ui/Layout.js';
import { Modal } from '../../components/ui/Modal.js';
import { Money } from '../../components/ui/Money.js';
import { Chip } from '../../components/ui/StatusPill.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { usePosContext } from '../../lib/pos.js';
import { supabase } from '../../lib/supabase.js';
import { useRecordRetailSale } from '../payments/useRecordRetailSale.js';

interface Product {
  id: string;
  name: string;
  price: number;
  category: string | null;
  tax_rate_code: TaxRateCode;
}

interface CartLine extends Product {
  qty: number;
}

export function KasaTab(): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const pos = usePosContext();

  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [fiscalizeOpen, setFiscalizeOpen] = useState(false);

  const products = useQuery({
    queryKey: ['products', tenantId, 'pos'],
    enabled: !!tenantId,
    queryFn: async (): Promise<Product[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, category, tax_rate_code')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as Product[];
    },
  });

  // On-hand stock per product (summed across locations). Products without an
  // inventory row (e.g. non-tracked services) are absent from the map and not gated.
  const inventory = useQuery({
    queryKey: ['inventory_qty', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<{ product_id: string; quantity_on_hand: number }[]> => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('product_id, quantity_on_hand');
      if (error) throw error;
      return (data ?? []) as unknown as { product_id: string; quantity_on_hand: number }[];
    },
  });

  const qtyByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of inventory.data ?? []) {
      m.set(r.product_id, (m.get(r.product_id) ?? 0) + Number(r.quantity_on_hand));
    }
    return m;
  }, [inventory.data]);

  const filtered = useMemo(() => {
    const list = products.data ?? [];
    const term = search.trim().toLowerCase();
    return term ? list.filter((p) => p.name.toLowerCase().includes(term)) : list;
  }, [products.data, search]);

  const total = useMemo(() => cart.reduce((s, l) => s + l.price * l.qty, 0), [cart]);

  /** Returns true if `wantQty` units of a tracked product would exceed on-hand. */
  function exceedsStock(productId: string, wantQty: number): boolean {
    const onHand = qtyByProduct.get(productId);
    return onHand !== undefined && wantQty > onHand;
  }

  function add(p: Product): void {
    const inCart = cart.find((l) => l.id === p.id)?.qty ?? 0;
    if (exceedsStock(p.id, inCart + 1)) {
      toast.error(t('retail.outOfStock', { name: p.name }));
      return;
    }
    setCart((c) => {
      const found = c.find((l) => l.id === p.id);
      if (found) return c.map((l) => (l.id === p.id ? { ...l, qty: l.qty + 1 } : l));
      return [...c, { ...p, qty: 1 }];
    });
  }

  function setQty(id: string, delta: number): void {
    if (delta > 0) {
      const line = cart.find((l) => l.id === id);
      if (line && exceedsStock(id, line.qty + delta)) {
        toast.error(t('retail.outOfStock', { name: line.name }));
        return;
      }
    }
    setCart((c) =>
      c.map((l) => (l.id === id ? { ...l, qty: l.qty + delta } : l)).filter((l) => l.qty > 0),
    );
  }

  const sale = useRecordRetailSale(() => {
    setCart([]);
    setFiscalizeOpen(false);
  });

  function submitSale(fiscalize: boolean): void {
    sale.mutate({
      items: cart.map((l) => ({
        product_id: l.id,
        name: l.name,
        quantity: l.qty,
        unit_price: l.price,
        tax_rate_code: l.tax_rate_code,
        total: l.price * l.qty,
      })),
      fiscalize,
    });
  }

  // Checkout is allowed when cart is non-empty and the minimum POS context is present
  // (location + staff). The shared hook enforces device requirement only when fiscalize=true.
  const missingCore = pos.missing.filter((m) => m !== 'device');
  const checkoutDisabled = cart.length === 0 || missingCore.length > 0;

  return (
    <>
      <div className="grid gap-5 lg:grid-cols-3">
        {/* Product grid */}
        <div className="lg:col-span-2">
          <div className="mb-3">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('retail.searchProducts')}
              aria-label={t('retail.searchProducts')}
            />
          </div>
          {products.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={ShoppingCart}
              title={t('retail.noProducts')}
              hint={t('retail.noProductsHint')}
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {filtered.map((p) => {
                const onHand = qtyByProduct.get(p.id);
                const tracked = onHand !== undefined;
                const out = tracked && onHand <= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => add(p)}
                    className={`group flex flex-col justify-between rounded-xl border bg-surface p-3 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                      out
                        ? 'border-danger ring-1 ring-danger/40'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="line-clamp-2 text-sm font-medium text-fg">{p.name}</span>
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-primary/15 text-primary opacity-0 transition-opacity group-hover:opacity-100">
                        <Plus className="h-4 w-4" />
                      </span>
                    </div>
                    <div className="mt-3 flex items-end justify-between">
                      <Money minor={p.price} className="text-base font-semibold" />
                      {p.category ? <Chip tone="neutral">{p.category}</Chip> : null}
                    </div>
                    {tracked ? (
                      out ? (
                        <span className="mt-1.5 text-xs font-semibold text-danger">
                          {t('retail.noStock')}
                        </span>
                      ) : (
                        <span className="mt-1.5 text-xs text-muted">
                          {t('retail.inStock')}:{' '}
                          <span className="tabular-nums text-fg">{onHand}</span>
                        </span>
                      )
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Cart */}
        <div>
          <div className="sticky top-20 flex max-h-[calc(100vh-7rem)] flex-col rounded-xl border border-border bg-surface">
            <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
              <ShoppingCart className="h-4 w-4 text-primary" aria-hidden />
              <h3 className="font-display text-base font-semibold text-fg">{t('retail.cart')}</h3>
              {cart.length > 0 ? (
                <Chip tone="neutral">{cart.reduce((s, l) => s + l.qty, 0)}</Chip>
              ) : null}
            </div>

            <div className="flex-1 overflow-y-auto">
              {cart.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-muted">{t('retail.cartEmpty')}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {cart.map((l) => (
                    <li key={l.id} className="flex items-center gap-2 px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-fg">{l.name}</p>
                        <Money minor={l.price * l.qty} muted className="text-xs" />
                      </div>
                      <div className="flex items-center gap-1">
                        <IconButton
                          label={t('common.remove')}
                          variant="secondary"
                          className="h-7 w-7"
                          onClick={() => setQty(l.id, -1)}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </IconButton>
                        <span className="w-6 text-center text-sm tabular-nums">{l.qty}</span>
                        <IconButton
                          label={t('common.add')}
                          variant="secondary"
                          className="h-7 w-7"
                          onClick={() => setQty(l.id, 1)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </IconButton>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-border p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-muted">{t('retail.total')}</span>
                <Money minor={total} className="text-xl font-bold" />
              </div>
              {!pos.ready && !pos.loading && missingCore.length > 0 ? (
                <div className="mb-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{missingCore.map((m) => t(`billing.missing.${m}`)).join(', ')}</span>
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                {cart.length > 0 ? (
                  <IconButton
                    label={t('retail.clearCart')}
                    variant="ghost"
                    onClick={() => setCart([])}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                ) : null}
                <Button
                  variant="primary"
                  className="flex-1"
                  disabled={checkoutDisabled}
                  loading={sale.isPending}
                  onClick={() => setFiscalizeOpen(true)}
                >
                  {t('retail.checkout')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fiscalize prompt */}
      <Modal
        open={fiscalizeOpen}
        onOpenChange={(o) => {
          if (!sale.isPending) setFiscalizeOpen(o);
        }}
        title={t('billing.fiscalize.prompt')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" disabled={sale.isPending} onClick={() => submitSale(false)}>
              {t('billing.fiscalize.no')}
            </Button>
            <Button variant="primary" loading={sale.isPending} onClick={() => submitSale(true)}>
              {t('billing.fiscalize.yes')}
            </Button>
          </>
        }
      />
    </>
  );
}
